import { Button, Empty, Skeleton, Tag as AntTag } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TagAccessDetails } from '../../../interfaces/AccessMap';
import type Tag from '../../../interfaces/Tag';
import { AccessMapService } from '../../../services/accessMapApiService';
import { Icon } from '../../Core/Icon';
import ProtectionCard from './ProtectionCard';
import RecipientsList from './RecipientsList';

const accessMapService = new AccessMapService();

interface Props {
  tag: Tag;
}

export const TagAccessTab = ({ tag }: Props) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState<TagAccessDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    accessMapService.getTagAccess(tag.id).then((data) => {
      if (!cancelled) {
        setDetails(data);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tag.id]);

  if (isLoading || !details) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <Skeleton active paragraph={{ rows: 3 }} />
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  const { visibility, protections } = details;

  return (
    <div className="flex flex-col gap-6 p-2">
      <section>
        <h4 className="mb-2 text-base font-medium">{t('accessMap.visibility')}</h4>
        {visibility.isPublic ? (
          <AntTag color="green" className="text-sm">
            <Icon name="EyeIcon" className="mr-1 inline-block h-3.5 w-3.5" />
            {t('accessMap.allUsersInCompany')}
          </AntTag>
        ) : (
          <RecipientsList
            users={visibility.users}
            groups={visibility.groups}
            emptyText={t('accessMap.tagNotShared')}
          />
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-row items-center justify-between">
          <h4 className="text-base font-medium">
            {t('accessMap.appliedProtections')} ({protections.length})
          </h4>
        </div>

        {protections.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('accessMap.noProtectionsForTag')}
          >
            <Button
              type="primary"
              size="small"
              icon={<Icon name="ExternalLinkIcon" className="h-4 w-4" />}
              onClick={() => window.location.assign('/settings')}
            >
              {t('accessMap.configureProtection')}
            </Button>
          </Empty>
        ) : (
          <div className="flex flex-col gap-2">
            {protections.map((p) => (
              <ProtectionCard key={p.type} protection={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default TagAccessTab;
